import React from 'react';
import { Play, Pause, Plus, Trash2, RotateCcw, Repeat, Clapperboard, Gauge, Film, Clapperboard as Vid, Loader2 } from 'lucide-react';

const SPEEDS = [0.5, 1, 1.5, 2];

export default function PitchAnimator({
  frames, activeFrame, onSelectFrame, onAddFrame, onDeleteFrame, onUpdateFrame,
  playing, onPlay, onStop, speed, setSpeed, loop, setLoop, canAnimate, hint, readOnly, onExport, exporting,
}) {
  return (
    <div className="border-t border-white/10 bg-[#0d1219] px-3 py-2.5" data-testid="pitch-animator-bar">
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-400">
          <Clapperboard className="h-3.5 w-3.5" /> التحريك
        </span>

        <button
          onClick={playing ? onStop : onPlay}
          disabled={!canAnimate}
          data-testid="pitch-anim-play-btn"
          className={`flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-bold transition-all active:scale-95 disabled:opacity-35 ${playing ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/25'}`}
        >
          {playing ? <><Pause className="h-3.5 w-3.5" /> إيقاف</> : <><Play className="h-3.5 w-3.5" /> تشغيل</>}
        </button>

        <div className="flex items-center gap-1 rounded-xl bg-white/5 p-1" data-testid="pitch-anim-frames">
          <button onClick={() => onSelectFrame(0)} data-testid="pitch-anim-frame-0"
            className={`h-7 min-w-7 rounded-lg px-2 text-[11px] font-bold transition-all ${activeFrame === 0 ? 'bg-emerald-500 text-white' : 'text-slate-300 hover:bg-white/10'}`}>
            البداية
          </button>
          {frames.map((f, i) => (
            <button key={f.id} onClick={() => onSelectFrame(i + 1)} data-testid={`pitch-anim-frame-${i + 1}`}
              className={`h-7 min-w-7 rounded-lg px-2 text-[11px] font-bold transition-all ${activeFrame === i + 1 ? 'bg-emerald-500 text-white' : 'text-slate-300 hover:bg-white/10'}`}>
              {i + 1}
            </button>
          ))}
          {!readOnly && <button onClick={onAddFrame} title="إضافة مشهد بالمواضع الحالية" data-testid="pitch-anim-add-frame-btn"
            className="flex h-7 w-7 items-center justify-center rounded-lg text-emerald-400 transition-all hover:bg-white/10 active:scale-90">
            <Plus className="h-3.5 w-3.5" />
          </button>}
        </div>

        {activeFrame > 0 && !readOnly && (
          <>
            <label className="flex items-center gap-1.5 rounded-xl bg-white/5 px-2 py-1 text-[11px] text-slate-300">
              مدة الانتقال
              <input
                type="number" min="0.3" max="10" step="0.1"
                value={(frames[activeFrame - 1]?.duration_ms || 1500) / 1000}
                onChange={(e) => onUpdateFrame(activeFrame - 1, { duration_ms: Math.max(300, Number(e.target.value) * 1000) })}
                data-testid="pitch-anim-duration-input"
                className="w-14 rounded-md border border-white/10 bg-[#111826] px-1.5 py-0.5 text-center text-[11px] text-white outline-none"
              />
              ث
            </label>
            <button onClick={() => onDeleteFrame(activeFrame - 1)} data-testid="pitch-anim-delete-frame-btn"
              className="flex h-7 w-7 items-center justify-center rounded-lg text-red-400 transition-all hover:bg-red-500/15 active:scale-90">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </>
        )}

        <div className="flex items-center gap-1 rounded-xl bg-white/5 p-1">
          <Gauge className="h-3.5 w-3.5 text-slate-400" />
          {SPEEDS.map((s) => (
            <button key={s} onClick={() => setSpeed(s)} data-testid={`pitch-anim-speed-${s}`}
              className={`h-6 rounded-lg px-1.5 text-[10.5px] font-bold transition-all ${speed === s ? 'bg-white/90 text-slate-900' : 'text-slate-300 hover:bg-white/10'}`}>
              {s}×
            </button>
          ))}
        </div>

        <button onClick={() => setLoop(!loop)} title="تكرار" data-testid="pitch-anim-loop-btn"
          className={`flex h-7 w-7 items-center justify-center rounded-lg transition-all active:scale-90 ${loop ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-400 hover:bg-white/10'}`}>
          <Repeat className="h-3.5 w-3.5" />
        </button>
        <span className="mx-0.5 hidden h-5 w-px bg-white/10 sm:block" />

        <button onClick={() => onExport('gif')} disabled={!canAnimate || Boolean(exporting)} data-testid="pitch-anim-export-gif-btn"
          title="تصدير صورة متحركة GIF"
          className="flex items-center gap-1.5 rounded-xl border border-white/15 bg-white/5 px-2.5 py-1.5 text-[11px] font-bold text-white/85 transition-all hover:bg-white/15 active:scale-95 disabled:opacity-35">
          {exporting === 'gif' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Film className="h-3.5 w-3.5" />} GIF
        </button>
        <button onClick={() => onExport('video')} disabled={!canAnimate || Boolean(exporting)} data-testid="pitch-anim-export-video-btn"
          title="تصدير مقطع فيديو WebM"
          className="flex items-center gap-1.5 rounded-xl border border-white/15 bg-white/5 px-2.5 py-1.5 text-[11px] font-bold text-white/85 transition-all hover:bg-white/15 active:scale-95 disabled:opacity-35">
          {exporting === 'video' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Vid className="h-3.5 w-3.5" />} فيديو
        </button>

        <button onClick={onStop} title="إرجاع للبداية" data-testid="pitch-anim-reset-btn"
          className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 transition-all hover:bg-white/10 active:scale-90">
          <RotateCcw className="h-3.5 w-3.5" />
        </button>
      </div>
      {exporting && <p className="mt-1.5 text-[10.5px] font-bold text-emerald-300" data-testid="pitch-anim-export-status">جارٍ تجهيز {exporting === 'gif' ? 'الصورة المتحركة' : 'المقطع'}... لا تغلق الصفحة</p>}
      {hint && <p className="mt-1.5 text-[10.5px] leading-relaxed text-slate-400" data-testid="pitch-anim-hint">{hint}</p>}
    </div>
  );
}
